import { useId } from "react";
import { Loader2, WifiOff, FolderOpen } from "lucide-react";

const VARIANTS = {
  primary: "bg-[#D4AF37] text-[#121212] hover:bg-[#E2C25A] shadow-[0_8px_24px_-12px_rgba(212,175,55,0.6)]",
  secondary: "bg-[#2A2A2A] text-white border border-[#555555] hover:border-[#D4AF37]/70 hover:bg-[#303030]",
  ghost: "bg-transparent text-white hover:bg-white/5",
  danger: "bg-transparent text-[#EB5757] border border-[#EB5757] hover:bg-[#EB5757]/10",
  success: "bg-[#27AE60] text-white hover:bg-[#2EC16C]",
};

export function Button({ title, onClick, variant = "primary", loading, disabled, icon: Icon, testID, className = "", size = "md", type = "button" }) {
  const off = disabled || loading;
  return (
    <button
      type={type}
      data-testid={testID}
      disabled={off}
      onClick={onClick}
      className={`focus-ring inline-flex items-center justify-center gap-2 rounded-xl px-4 font-bold transition-all duration-200 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 ${
        size === "lg" ? "min-h-[58px] text-base tracking-[0.8px]" : "min-h-12 text-[15px]"
      } ${VARIANTS[variant]} ${className}`}
    >
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin" aria-label="Chargement" />
      ) : (
        <>
          {Icon && <Icon className={size === "lg" ? "h-[22px] w-[22px]" : "h-[18px] w-[18px]"} aria-hidden />}
          <span>{title}</span>
        </>
      )}
    </button>
  );
}

export function Field({ label, error, testID, multiline, onChangeText, className = "", ...rest }) {
  const id = useId();
  const Tag = multiline ? "textarea" : "input";
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="t-label">
        {label}
      </label>
      <Tag
        id={id}
        data-testid={testID}
        onChange={(e) => onChangeText?.(e.target.value)}
        aria-invalid={!!error}
        className={`w-full rounded-xl border bg-[#2A2A2A] px-3 text-[15px] text-white placeholder:text-[#9E9E9E] outline-none transition-colors focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 disabled:opacity-60 ${
          error ? "border-[#EB5757]" : "border-[#333333]"
        } ${multiline ? "min-h-[110px] py-3" : "h-12"} ${className}`}
        {...rest}
      />
      {error ? (
        <span className="t-caption !text-[#EB5757]" data-testid={`${testID}-error`}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function Card({ children, className = "", testID, style }) {
  return (
    <div data-testid={testID} className={`card ${className}`} style={style}>
      {children}
    </div>
  );
}

export function Badge({ label, color, testID }) {
  return (
    <span
      data-testid={testID}
      className="inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium leading-4"
      style={{ borderColor: color, color }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

export function IconButton({ icon: Icon, onClick, testID, badge, label }) {
  return (
    <button
      type="button"
      data-testid={testID}
      onClick={onClick}
      aria-label={label}
      title={label}
      className="focus-ring relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#333333] bg-[#1E1E1E] text-white transition-colors hover:border-[#D4AF37]/70 hover:text-[#D4AF37]"
    >
      <Icon className="h-[22px] w-[22px]" aria-hidden />
      {badge ? (
        <span data-testid={`${testID}-badge`} className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#D4AF37] px-[3px] text-[10px] font-bold text-[#121212]">
          {badge > 9 ? "9+" : badge}
        </span>
      ) : null}
    </button>
  );
}

export function Loading() {
  return (
    <div data-testid="loading-indicator" className="flex flex-1 items-center justify-center p-8">
      <Loader2 className="h-9 w-9 animate-spin text-[#D4AF37]" aria-label="Chargement" />
    </div>
  );
}

export function ErrorView({ message, onRetry }) {
  return (
    <div data-testid="error-view" className="flex flex-col items-center gap-4 p-6 text-center">
      <WifiOff className="h-9 w-9 text-white" aria-hidden />
      <p className="t-body">{message}</p>
      <Button testID="retry-button" title="Réessayer" variant="secondary" onClick={onRetry} />
    </div>
  );
}

export function Empty({ icon: Icon = FolderOpen, title, text }) {
  return (
    <div data-testid="empty-state" className="flex flex-col items-center gap-2 p-6 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#292415]">
        <Icon className="h-7 w-7 text-[#D4AF37]" aria-hidden />
      </div>
      <h3 className="t-h3">{title}</h3>
      {text ? <p className="t-body max-w-md">{text}</p> : null}
    </div>
  );
}

export function ChipRow({ options, value, onChange, testIDPrefix, className = "" }) {
  return (
    <div role="tablist" className={`no-scrollbar flex items-center gap-2 overflow-x-auto py-2.5 ${className}`}>
      {options.map((o) => {
        const active = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`${testIDPrefix}-${o.key}`}
            onClick={() => onChange(o.key)}
            className={`focus-ring h-9 shrink-0 rounded-full border px-4 text-[13px] font-medium transition-colors ${
              active ? "border-[#D4AF37] bg-[#292415] text-[#D4AF37]" : "border-[#333333] bg-[#1E1E1E] text-[#CCCCCC] hover:border-[#555555] hover:text-white"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Stat({ label, value, icon: Icon, accent = "#D4AF37", testID }) {
  return (
    <div data-testid={testID} className="rounded-[20px] border border-[#333333] bg-[#1E1E1E] p-4 transition-colors hover:border-[#555555]">
      <Icon className="h-[18px] w-[18px]" style={{ color: accent }} aria-hidden />
      <div className="t-number mt-2" data-testid={`${testID}-value`}>
        {value}
      </div>
      <div className="t-caption line-clamp-2">{label}</div>
    </div>
  );
}

export function Row({ label, value, testID }) {
  return (
    <div className="flex justify-between gap-3 py-1.5">
      <span className="t-caption shrink-0">{label}</span>
      <span data-testid={testID} className="t-body min-w-0 break-words text-right">
        {value === null || value === undefined || value === "" ? "—" : String(value)}
      </span>
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, testID, icon: Icon }) {
  return (
    <label className="flex items-center gap-2 rounded-xl border border-[#333333] bg-[#2A2A2A] px-3 transition-colors focus-within:border-[#D4AF37]">
      <Icon className="h-[18px] w-[18px] text-[#9E9E9E]" aria-hidden />
      <span className="sr-only">{placeholder}</span>
      <input
        data-testid={testID}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-11 flex-1 bg-transparent text-white outline-none placeholder:text-[#9E9E9E]"
      />
    </label>
  );
}
